import { Link, useSearchParams } from 'react-router';
import { ALL_SEASONS } from '@gl/shared';

import { useHome } from '../api/hooks.js';
import { useCan } from '../auth.js';
import { ErrorState, Loading, LoginPrompt } from '../components/states.js';
import { PollCard } from '../components/PollCard.js';
import { ImportantDatesCard } from '../components/ImportantDatesCard.js';
import { Logo } from '../components/Logo.js';
import { Markdown } from '../components/Markdown.js';
import { formatDate } from '../util/format.js';

const LOGIN_ERRORS: Record<string, string> = {
  cancelled: 'Sign-in was cancelled.',
  state: 'That sign-in link expired. Please try again.',
  session: 'We could not start a session. Please try again.',
  not_invited: 'This site is invite only.',
  unconfigured: 'Sign-in is not configured on this server.',
};

function LoginBanner() {
  const [params, setParams] = useSearchParams();
  const code = params.get('login_error');
  if (!code) return null;

  const message = params.get('login_message') ?? LOGIN_ERRORS[code] ?? 'Sign-in failed.';

  return (
    <div className="mb-6 flex items-start gap-3 border-l-4 border-brass-400 bg-field-900 px-4 py-3 text-sm text-brass-200">
      <span className="flex-1">{message}</span>
      <button
        type="button"
        className="text-brass-300 hover:text-brass-200"
        onClick={() => {
          params.delete('login_error');
          params.delete('login_message');
          setParams(params, { replace: true });
        }}
      >
        Dismiss
      </button>
    </div>
  );
}

export function HomePage() {
  const { data, isPending, error } = useHome();
  const canEditDates = useCan('edit_important_dates');

  // The newest post is shown in full; the two behind it as titles. The server
  // sends exactly these three.
  const [latest, ...earlier] = data?.news ?? [];

  return (
    <div>
      <LoginBanner />

      {/*
        The masthead is the open of the broadcast: the whole lockup at the size
        it was drawn for, the league's name set as large as the page can carry
        it, and a raked volt stripe field behind both.
      */}
      <header className="bleed relative mb-10 overflow-hidden border-b-2 border-brass-400 bg-field-900 py-8">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.13]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(115deg, #a5d62e 0 2px, transparent 2px 16px)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-field-900 to-transparent"
        />
        {/* The crest again as a network watermark, bled off the right edge. */}
        <Logo
          title=""
          className="pointer-events-none absolute -right-10 -top-16 hidden h-[150%] w-auto
            opacity-[0.08] lg:block"
        />

        <div className="relative flex flex-col items-start gap-6 sm:flex-row sm:items-center">
          <div className="shrink-0 border-2 border-brass-400 bg-field-950 p-3">
            <Logo title="" className="h-28 w-auto sm:h-40" />
          </div>

          <div className="min-w-0">
            <p className="eyebrow mb-2 inline-block bg-brass-400 px-2 py-0.5 text-field-950">
              Dynasty fantasy football · Est. 2024
            </p>
            <h1
              className="font-display text-6xl font-bold uppercase leading-[0.82] tracking-tight
                text-field-50 sm:text-7xl lg:text-8xl xl:text-[7.5rem]"
            >
              Gentlemen&apos;s
              <span className="block text-brass-400">League</span>
            </h1>
          </div>
        </div>
      </header>

      {isPending ? <Loading /> : null}
      {error ? <ErrorState error={error} /> : null}

      {data ? (
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <section>
              <div className="mb-3 flex items-baseline justify-between">
                <h2 className="section-title">Latest news</h2>
                <Link to="/news" className="eyebrow hover:text-brass-200">
                  All news →
                </Link>
              </div>

              {latest ? (
                <div className="space-y-3">
                  {/*
                    An article rather than one big link: the body is real
                    markdown, and a post that links somewhere would otherwise put
                    an anchor inside an anchor.
                  */}
                  <article className="card border-l-2 border-l-brass-400">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="font-display text-3xl font-bold uppercase leading-none tracking-tight">
                        <Link
                          to={`/news/${latest.slug}`}
                          className="text-field-50 hover:text-brass-300"
                        >
                          {latest.title}
                        </Link>
                      </h3>
                      {latest.publishedAt ? (
                        <time className="shrink-0 text-xs text-field-500">
                          {formatDate(latest.publishedAt)}
                        </time>
                      ) : (
                        <span className="badge bg-brass-700/30 text-brass-200">Draft</span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-field-500">{latest.authorName}</p>

                    <div className="mt-3">
                      <Markdown>{latest.body}</Markdown>
                    </div>
                  </article>

                  {/*
                    The two behind it are titles only. The point of the home page
                    is the newest post; these are there so nobody has to open the
                    news page to find out whether they have missed anything.
                  */}
                  {earlier.length > 0 ? (
                    <div className="card">
                      <h3 className="rail-title">Earlier</h3>
                      <ul className="divide-y divide-field-800">
                        {earlier.map((post) => (
                          <li
                            key={post.id}
                            className="flex items-baseline justify-between gap-3 py-2 text-sm first:pt-0 last:pb-0"
                          >
                            <Link
                              to={`/news/${post.slug}`}
                              className="min-w-0 font-display text-base font-semibold uppercase
                                tracking-tight text-field-200 hover:text-brass-300"
                            >
                              {post.title}
                            </Link>
                            {post.publishedAt ? (
                              <time className="shrink-0 text-xs text-field-500">
                                {formatDate(post.publishedAt)}
                              </time>
                            ) : (
                              <span className="badge bg-brass-700/30 text-brass-200">Draft</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="card text-sm text-field-400">No posts yet.</div>
              )}
            </section>
          </div>

          <div className="space-y-6 lg:sticky lg:top-[6.5rem] lg:self-start">
            <section>
              <h2 className="section-title mb-3">Statistic of the week</h2>
              {data.featuredStat ? (
                <Link
                  to={data.featuredStat.href}
                  className="block border border-field-800 bg-field-900 hover:border-brass-400"
                >
                  <div className="flex items-center justify-between gap-3 bg-field-800 px-4 py-1.5">
                    <p className="font-display text-xs font-semibold uppercase tracking-bug text-brass-300">
                      {data.featuredStat.title}
                    </p>
                    <span className="shrink-0 font-display text-xs font-semibold uppercase tracking-bug text-field-400">
                      {data.featuredStat.season === ALL_SEASONS
                        ? 'All seasons'
                        : data.featuredStat.season}
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="font-display text-2xl font-semibold uppercase leading-[1.05] tracking-tight text-field-50">
                      {data.featuredStat.summary}
                    </p>
                    <p className="eyebrow mt-3">See the full table →</p>
                  </div>
                </Link>
              ) : (
                <div className="card text-sm text-field-400">
                  Statistics arrive once the first nightly calculation has run.
                </div>
              )}
            </section>

            <section>
              <h2 className="section-title mb-3">Upcoming dates</h2>
              {data.requiresLogin.importantDates ? (
                <LoginPrompt>Log in to see important league dates.</LoginPrompt>
              ) : (
                <ImportantDatesCard dates={data.upcomingDates} canEdit={canEditDates} />
              )}
            </section>
          </div>
        </div>
      ) : null}

      {/*
        Polls run the full width under the two-column block: at broadcast widths
        a vote bar is a graphic, and a graphic wants the whole screen rather
        than two thirds of it.
      */}
      {data ? (
        <section className="mt-8">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="section-title">Active polls</h2>
            <Link to="/polls" className="eyebrow hover:text-brass-200">
              All polls →
            </Link>
          </div>

          {data.requiresLogin.polls ? (
            <LoginPrompt>Log in to vote in league polls.</LoginPrompt>
          ) : data.activePolls.length === 0 ? (
            <div className="card text-sm text-field-400">No polls are open right now.</div>
          ) : (
            <div className="grid gap-5 xl:grid-cols-2">
              {data.activePolls.map((poll) => (
                <PollCard key={poll.id} poll={poll} />
              ))}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
